import { useEffect, useRef, useCallback } from "react";

export default function useIdleLogout({
  enabled = false,
  idleMs = 30 * 60 * 1000,
  warnMs = 60 * 1000,
  onWarn = () => {},
  onLogout = () => {},
} = {}) {
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);

  const clearTimers = () => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    warnTimer.current = null;
    logoutTimer.current = null;
  };

  const reset = useCallback(() => {
    if (!enabled) {
      clearTimers();
      return;
    }
    if (document.visibilityState === "hidden") return;

    clearTimers();
    const untilWarn = Math.max(0, idleMs - warnMs);

    warnTimer.current = setTimeout(() => {
      onWarn({ warnMs, idleMs });
    }, untilWarn);

    logoutTimer.current = setTimeout(() => {
      onLogout();
    }, idleMs);
  }, [enabled, idleMs, warnMs, onWarn, onLogout]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    const bump = () => reset();
    const EVENTS = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "wheel",
    ];

    reset();
    EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    document.addEventListener("visibilitychange", bump);

    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener("visibilitychange", bump);
      clearTimers();
    };
  }, [enabled, idleMs, warnMs, onWarn, onLogout, reset]);

  return { reset };
}
